import React, { useState } from 'react';
import Script from 'next/script';

import { IVideoBlockFields } from '../../../types/contentful';

import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { getPartnerTrackingProperties } from '../../utils/getPartnerTrackingProperties';

import { Lightbox } from './Lightbox';
import { ScrollReveal } from '../ScrollReveal';

export function VideoBlock({
  title,
  vimeoId,
  backgroundImage
}: IVideoBlockFields) {
  const [showingVideo, setShowingVideo] = useState(false);

  const toggleVideo = (displayVideo: boolean) => {
    setShowingVideo(displayVideo);
    if (displayVideo) {
      trackWithFingerprint('Video Opened', {
        page_url: window.location.href,
        ...getPartnerTrackingProperties()
      });
    } else {
      trackWithFingerprint('Video Closed', {
        page_url: window.location.href,
        ...getPartnerTrackingProperties()
      });
      document.getElementById('gl-video-play-button')?.focus();
    }
  };

  const { file } = backgroundImage?.fields ?? {};

  return (
    <>
      <Script src="https://player.vimeo.com/api/player.js" />
      <ScrollReveal>
        <div
          className="block-container my-12 tablet:my-16 relative bg-cover bg-center bg-[50%_50%] before:content-[''] before:absolute before:w-full before:h-full before:top-0 before:left-0 before:bg-[rgba(255,255,255,0.5)]"
          data-blockid="videoBlock"
          data-testid="video-block"
          style={{
            backgroundImage: file?.url ? `url(${file.url})` : undefined
          }}
        >
          <div className="layout py-16 tablet:py-32 desktop:py-[10.375rem]">
            <div className="column w-3/12 tablet:w-2/12 desktop:py-16 desktop:px-0">
              <button
                id="gl-video-play-button"
                onClick={(e) => {
                  toggleVideo(true);
                  e.currentTarget.blur();
                }}
                aria-label="Play video"
                className="z-[1] outline-0 border-0 appearance-none -webkit-appearance-none p-0 m-0 cursor-pointer bg-transparent [&_.playIconBackground]:transition-[fill_0.2s_ease] [&_.playIconCenter]:transition-[fill_0.2s_ease] hover:[&_.playIconBackground]:fill-[#80D6B0] focus:[&_.playIconBackground]:fill-[#80D6B0] hover:[&_.playIconCenter]:fill-[#035C67] focus:[&_.playIconCenter]:fill-[#035C67]"
              >
                <svg
                  width="221"
                  height="221"
                  viewBox="0 0 221 221"
                  aria-hidden="true"
                  className="cursor-pointer max-w-full w-full h-full z-0"
                >
                  <path
                    className="playIconBackground"
                    d="M0 56C0 25.072 25.0721 0 56 0H164.482C195.41 0 220.482 25.0721 220.482 56V164.482C220.482 195.41 195.41 220.482 164.482 220.482H56C25.072 220.482 0 195.41 0 164.482V56Z"
                    fill="#035C67"
                  />
                  <path
                    className="playIconCenter"
                    d="M145.086 102.266C150.419 105.345 150.419 113.043 145.086 116.123L97.2475 143.742C91.9141 146.821 85.2474 142.972 85.2474 136.814L85.2475 81.5748C85.2475 75.4164 91.9141 71.5674 97.2475 74.6466L145.086 102.266Z"
                    fill="#80D6B0"
                  />
                </svg>
              </button>
              {vimeoId && title && (
                <Lightbox
                  title={title}
                  showingVideo={showingVideo}
                  setShowingVideo={toggleVideo}
                  vimeoId={vimeoId}
                  aria-label="video"
                />
              )}
            </div>
          </div>
        </div>
      </ScrollReveal>
    </>
  );
}
